/*
 * PORTED FROM cres-t5 `src/components/Catalog/FormRenderer.tsx`. Kept textually as close to it as
 * possible -- only the axios helper and the endpoint differ -- because two form renderers that
 * drift would draw the same record two ways. If you change one, change the other.
 *
 * t1 draws a record of an OBJECT; t5 draws one of its own Records screen. Both ask the SAME service
 * for the definition, so what a field means has one answer.
 */
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Checkbox, FormControlLabel, MenuItem, Radio, Select, TextField,
        Typography} from "@mui/material";
import Axios from "../../util/Axios";
import RepeatingGroup from "./RepeatingGroup";
import {FormLongSelect, LONG_LIST} from "./FormLongSelect";
import {FormObjectPicker, picksFromListScreen} from "./FormObjectPicker";
import Attachments from "../Common/Attachments";
import {nestedProblems, ownProblems, Problem} from "../../util/formProblems";
import {Grey6, Grey17} from "../../theme/styles";

// Drawing a record of a composite's shape.
//
// THE DEFINITION COMES FROM THE SERVER, WHOLE. Fields, the control each wants, and a reference's
// options all arrive together, so this component never works out which endpoint answers for which
// field — and what it offers is what a write is checked against. A renderer that resolved its own
// options would be re-deriving what the type already knows, and the two would drift.
//
// DEPENDENT FIELDS RE-FETCH ON CHANGE. A field whose options hang off another ("Capabilities, for
// whichever Supplier Type this record chose") cannot be resolved once at mount, so the definition is
// re-read when a value changes. That mirrors what PlatformSelect already does for its exclusion
// filters — it re-reads sibling values on every render — and is the same idea one layer out.
//
// REFUSALS ARE SHOWN, NOT COMPUTED. When a save is rejected the server says which fields and why,
// and those messages are drawn where they belong. Nothing here decides that a value is wrong: a
// client that re-derived the rules could contradict the answer that actually blocked the write.
//
// REQUIRED-NESS IS THE SERVER'S ANSWER, NOT THIS COMPONENT'S. A field can be required because of
// what another field says, so the rule is evaluated where the write will also evaluate it and only
// the verdict travels. Re-reading on change — which the dependent picker already made necessary —
// is what makes a field become required the moment its trigger is answered.
//
// A VALUE ALREADY HELD IS ALWAYS NAMED, even once the picker stops offering it — the server
// resolves it by key and sends it back marked. It is drawn disabled, so it can be seen and removed
// but not chosen again. Before this the renderer had a key and no name and drew the raw UUID, which
// neither says what was chosen nor admits it cannot say.
//
// THE LAYOUT COMES FROM THE FORM, and the ORDER arrives already applied. A client that sorted for
// itself would be a second opinion about what the form says, which is how two screens over one
// record come to disagree about which question comes first. This groups and draws; it does not
// decide.
//
// A REPEATING GROUP IS A GRID, one row per element and one column per field of the shape it holds.
// The group reads its own definition, so a cell and a field are drawn by the same control table.
//
// WHAT THIS DOES NOT DO YET, stated so nobody reads more into it: there is no presentation metadata
// anywhere, so controls are derived from types and fields are drawn in code order. Labels, sections,
// tabs and ordering are the next increment. A composite nested inside a grid row is still a note --
// a grid inside a grid is a layout nobody can read.

export type Field = {
    Code: string;
    Label: string;
    Description?: string | null;
    Control: string;
    TypeName: string;
    Cardinality: string;
    /** For a composite, the shape of ONE element -- a composite type is its structure rather than a
     *  pointer at one, which is what makes the grid's columns readable from the field itself. */
    DataTypeRevisionUSID: string;
    Retired: boolean;
    /** False when a showWhen rule on the form says not yet. Absent means visible. */
    Visible?: boolean;
    /** The layout says this field is drawn and not typed -- the server writes it (a lot's quantity
     *  on hand, its next count due). Disabled whatever the form's own mode is. */
    ReadOnly?: boolean;
    /** This record asks it and the shape does not — real data with no definition behind it. */
    Diverged?: boolean;
    /** The object type that declared this question. Absent for a record with no type line. */
    DeclaredBy?: DeclaredBy | null;
    /** The classification a reference picks records from, when its stored value is the object's
     *  USID. The chooser is then that classification's list screen. */
    PicksObjects?: string | null;
    // The view half. All of it optional: a record with no layout draws as it always did. `Label` is
    // not here -- the server resolves the form's override against the administrative name, so there
    // is one label and nothing to choose between.
    Help?: string | null;
    Placeholder?: string | null;
    Width?: string;
    Section?: string | null;
    Order?: number;
    // The VERDICT for this record, computed server-side — required-ness can depend on another
    // field's value, so it is only answerable against a particular set of values.
    Required?: boolean;
    // Offered false = the record holds this but the picker would not offer it now. Display is null
    // when even the name is gone, which is an orphan rather than a narrowing.
    Options: {Value: string; Display: string | null; Offered?: boolean; Orphan?: boolean;
              Attributes?: Record<string, string | null>}[];
    RequiresContext: string[];
    Unavailable?: string;
};

/** The object type that declared a question, when the record was seeded from a line of types. */
export type DeclaredBy = {ObjectTypeUSID: string; Name: string};

export type Section = {Key: string; Title: string; Description?: string | null;
                       /** Which tab this section sits under. Null means outside the tabs. */
                       Tab?: string | null;
                       /** Which object type contributed this section. Absent when there is no line. */
                       DeclaredBy?: DeclaredBy | null};
export type Tab = {Key: string; Title: string; Order?: number};
export type Display = {Kind: string; Text?: string | null; Section?: string | null; Order?: number};

export type {Problem};

/** The YYYY-MM-DD a stored date answer names, or "" -- see the Date control. */
function dayOf(value: any): string {
    if (value === null || value === undefined) return "";
    const match = /^(\d{4}-\d{2}-\d{2})(?:[ T].*)?$/.exec(String(value).trim());
    return match ? match[1] : "";
}

type Props = {
    /** A shape to preview. Give this or objectRevisionUSID, not both. */
    compositeRevisionUSID?: string;
    /** A record to draw, from its OWN contract — which may differ from the shape it came from. */
    objectRevisionUSID?: string;
    /** Starting values, keyed by field code. */
    value?: Record<string, any>;
    onChange?: (values: Record<string, any>) => void;
    readOnly?: boolean;
    /** Refusals from the last save attempt, drawn against the fields they name. */
    problems?: Problem[];
    /** The definition once loaded. For a caller drawing something BESIDE the form -- the type-line
     *  diagram -- so it describes the form on screen rather than fetching its own answer. */
    onLoaded?: (loaded: {fields: Field[]; sections: Section[]}) => void;
    /** Outline the sections one object type contributed. Set from the type-line diagram: clicking
     *  a level there answers "which of these questions are that level's" by SHOWING them, which a
     *  form of seventeen fields under four headings cannot otherwise say. */
    highlightTypeUSID?: string | null;
    /** The colour to outline them in. It comes from the box that was clicked, so the outline and
     *  that box are plainly the same thing -- this record's own level is violet, an ancestor's is
     *  blue, and the form says which without a legend. */
    highlightColor?: string;
};

export default function FormRenderer({compositeRevisionUSID, objectRevisionUSID, value, onChange,
                                      readOnly, problems, onLoaded, highlightTypeUSID,
                                      highlightColor = "#1997D6"}: Props) {
    const [fields, setFields] = useState<Field[]>([]);
    const [sections, setSections] = useState<Section[]>([]);
    const [tabs, setTabs] = useState<Tab[]>([]);
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const [display, setDisplay] = useState<Display[]>([]);
    const [values, setValues] = useState<Record<string, any>>(value ?? {});
    const [error, setError] = useState<string | null>(null);
    const [noShape, setNoShape] = useState(false);
    const [loading, setLoading] = useState(true);

    const announce = useRef(onLoaded);
    announce.current = onLoaded;

    // BRING THE LIT SECTION INTO VIEW. A form taller than the window is the normal case here --
    // four levels of questions -- so lighting a section the reader cannot see answers their click
    // with nothing at all. Only the FIRST lit section is tracked; the rest follow it on screen.
    const litSection = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!highlightTypeUSID) return;
        litSection.current?.scrollIntoView({behavior: "smooth", block: "center"});
    }, [highlightTypeUSID]);

    const load = useCallback((context: Record<string, any>) => {
        Axios({method: "POST", url: "t1/v1/object_form_definition",
                   data: objectRevisionUSID ? {ObjectRevisionUSID: objectRevisionUSID, Context: context}
                                            : {CompositeRevisionUSID: compositeRevisionUSID,
                                               Context: context}})
            .then((response: any) => {
                setFields(response?.data?.Fields ?? []);
                setSections(response?.data?.Sections ?? []);
                // Handed up, not re-fetched by whoever draws beside the form. See onLoaded.
                // THROUGH A REF, so the callback is not a dependency of `load`: callers pass an
                // inline function, which is a new value every render, and a re-created `load` is
                // a re-fetch on every render -- a loop that only shows up as the form flickering.
                announce.current?.({fields: response?.data?.Fields ?? [],
                                    sections: response?.data?.Sections ?? []});
                setTabs(response?.data?.Tabs ?? []);
                setDisplay(response?.data?.Display ?? []);
                setError(null);
                setNoShape(false);
                setLoading(false);
            })
            .catch((error: any) => {
                // A SENTENCE, NOT A CODE. The drawer on QR-00002 drew "CC-RCRD-SHAPE" in orange
                // (Ed, 2026-10-30): a record whose type names no shape has no form, which is a
                // state to say in words, quietly -- not a fault to flag by its code. Every other
                // refusal shows its Cause, which is the sentence the server wrote for a person.
                const code = error?.response?.data?.Code;
                if (code === "CC-RCRD-SHAPE") { setNoShape(true); setError(null); }
                else setError(error?.response?.data?.Cause ?? code ?? "The form could not be loaded.");
                setLoading(false);
            });
    }, [compositeRevisionUSID, objectRevisionUSID]);

    // Re-read when a value changes, because a dependent field's options depend on one. Keyed on the
    // serialised values rather than the object so an unchanged render does not refetch.
    const signature = JSON.stringify(values);
    useEffect(() => { load(JSON.parse(signature)); }, [load, signature]);

    const set = (code: string, next: any) => {
        const updated = {...values, [code]: next};
        setValues(updated);
        onChange?.(updated);
    };

    // A retired field is drawn only when it holds a value: the question is no longer asked, but the
    // answer somebody already gave should not vanish from the record.
    // WHAT IS DRAWN. Two different reasons a field is not:
    //
    //  * RETIRED -- the shape no longer asks it. Kept when the record still holds an answer, so an
    //    existing value stays readable and removable.
    //  * NOT VISIBLE -- a showWhen rule on the form says not yet. Evaluated SERVER-SIDE against the
    //    same values that decide required-ness, so the two cannot disagree about a condition.
    //
    // A HIDDEN FIELD IS STILL REQUIRED IF ITS RULE SAYS SO. The save walks the record's contract and
    // never consults this, so hiding cannot be used to dodge a requirement -- it would refuse the
    // write and name a field nobody can see, which is why the designer refuses a rule that could do
    // that. Visible !== false so a definition that predates this draws exactly as before.
    const visible = fields.filter(f => f.Visible !== false)
                          .filter(f => !f.Retired || values[f.Code] != null);

    // GROUPED, NOT SORTED. The server already ordered the fields; this only cuts them into the
    // sections the form declared, keeping that order. An unsectioned field lands in a leading group
    // with no heading, which is exactly how a form with no layout has always looked.
    const groups = (() => {
        const keys = [null as string | null, ...sections.map(s => s.Key)];
        return keys
            .map(key => ({
                key: key ?? "__none",
                section: sections.find(s => s.Key === key) ?? null,
                fields: visible.filter(f => (f.Section ?? null) === key),
                display: display.filter(d => (d.Section ?? null) === key),
            }))
            .filter(g => g.fields.length > 0 || g.display.length > 0);
    })();

    // WHICH TAB IS OPEN. Defaults to the first, and falls back to it if the open one disappears --
    // a tab removed while somebody is looking at it must not leave a blank form.
    const tabKeys = tabs.map(t => t.Key);
    const openTab = activeTab && tabKeys.includes(activeTab) ? activeTab : (tabKeys[0] ?? null);

    // THE LEAF IS THE LAST TYPE TO DECLARE ANYTHING. The contract is concatenated root first, so
    // whichever type declared the final field is the one the record actually IS -- everything above
    // it was inherited. Derived rather than passed in, because the renderer already holds the whole
    // contract and a second source for "which type is this" would be one more thing to keep in step.
    //
    // Undefined for a record with no line, which makes every comparison below false and draws the
    // form exactly as it always did.
    const leafTypeName = [...fields].reverse().find(f => f.DeclaredBy)?.DeclaredBy?.Name;

    // A SECTION OUTSIDE THE TABS IS ALWAYS SHOWN, above them. That is what makes adding a tab safe:
    // sections that predate it keep appearing rather than vanishing into a tab nobody selected, and
    // it gives the useful shape of standing fields above tabbed detail without a second mechanism.
    const shown = groups.filter(g => {
        if (!tabs.length) return true;
        const tab = g.section?.Tab ?? null;
        return tab === null || tab === openTab;
    });

    // CONSECUTIVE SECTIONS FROM ONE TYPE, AS ONE BLOCK. The form is the line concatenated root
    // first, so a level's sections are already adjacent; this only says where one level's
    // contribution ends. Built from the order as it stands rather than by sorting -- if a type's
    // sections were ever NOT adjacent, this draws a box round each run, which is what it does
    // today anyway.
    const runs: {key: string; typeUSID: string; groups: typeof shown}[] = [];
    for (const group of shown) {
        const typeUSID = String(group.section?.DeclaredBy?.ObjectTypeUSID ?? "");
        const last = runs[runs.length - 1];
        if (last && last.typeUSID === typeUSID) last.groups.push(group);
        else runs.push({key: `${typeUSID}:${group.key}`, typeUSID, groups: [group]});
    }

    if (loading) return <Typography style={{fontSize: 14, color: Grey6}}>Loading…</Typography>;
    if (noShape) return <Typography style={{fontSize: 13, color: Grey6}}>This record has no form: its type names no shape.</Typography>;
    if (error) return <Typography style={{fontSize: 14, color: "#C9820A"}}>{error}</Typography>;

    return (
        // THE WIDTH IS PER FIELD, not on the container. A single input reads badly stretched across
        // a wide screen, so those stay in a 720px column -- but a repeating group is a grid with a
        // column per field of its shape, and a contact with eight of them cannot live in that
        // column. Capping the container made every group scroll horizontally to show its fourth
        // column, which is the shape of the data losing an argument with the layout.
        <div style={{display: "flex", flexDirection: "column", gap: SECTION_GAP}}>
            {visible.length === 0 ? (
                <Typography style={{fontSize: 14, color: Grey6}}>
                    This shape has no fields yet.
                </Typography>
            ) : null}

            {tabs.length > 0 ? (
                <div style={{display: "flex", gap: 22, borderBottom: "1px solid #D8DDE4"}}>
                    {tabs.map(tab => (
                        <div key={tab.Key}
                             onClick={() => setActiveTab(tab.Key)}
                             style={{cursor: "pointer", paddingBottom: 6, fontSize: 14,
                                     fontWeight: openTab === tab.Key ? 600 : 400,
                                     color: openTab === tab.Key ? Grey17 : Grey6,
                                     borderBottom: openTab === tab.Key
                                         ? "2px solid #1565C0" : "2px solid transparent"}}>
                            {tab.Title}
                        </div>
                    ))}
                </div>
            ) : null}

            {runs.map(run => {
                // ONE OUTLINE PER TYPE, NOT PER SECTION. A level contributes as many sections as it
                // likes -- Bone Screw contributes Detail and Control -- and drawing a box round
                // each says "here are two things" when the diagram just said "here is one level".
                // Ed, 2026-10-18: "Is it possible to outline the whole section (both sections
                // rather than individually)?"
                const lit = !!highlightTypeUSID && run.typeUSID === String(highlightTypeUSID);
                return (
                <div key={run.key}
                     ref={lit ? litSection : undefined}
                     style={{display: "flex", flexDirection: "column", gap: SECTION_GAP,
                             // THE SAME GAP THE CONTAINER USES, so wrapping a run changes where the
                             // outline goes and nothing else: sections inside a run stay exactly as
                             // far apart as sections either side of it.
                             //
                             // Drawn OUTSIDE the content with an offset outline rather than a
                             // border, so lighting a level does not move anything on the form --
                             // a layout that shifts when you click a diagram reads as a bug.
                             outline: lit ? `2px solid ${highlightColor}` : undefined,
                             outlineOffset: lit ? 10 : undefined,
                             borderRadius: lit ? 4 : undefined,
                             transition: "outline-color 120ms ease"}}>
                {run.groups.map(group => (
                <div key={group.key}
                     style={{display: "flex", flexDirection: "column", gap: 18}}>
                    {group.section ? (
                        <div style={{borderBottom: "1px solid #D8DDE4", paddingBottom: 6, marginTop: 8}}>
                            <Typography style={{fontSize: 15, fontWeight: 600, color: Grey17}}>
                                {group.section.Title}
                                {group.section.DeclaredBy && group.section.DeclaredBy.Name !== leafTypeName ? (
                                    // The heading says which type's questions these are, so a stack
                                    // of four ancestors' sections reads as a line rather than as one
                                    // very long form.
                                    <span style={{fontSize: 13, fontWeight: 400, color: Grey6}}>
                                        {" "}· from {group.section.DeclaredBy.Name}
                                    </span>
                                ) : null}
                            </Typography>
                            {group.section.Description ? (
                                <Typography style={{fontSize: 13, color: Grey6, marginTop: 2}}>
                                    {group.section.Description}
                                </Typography>
                            ) : null}
                        </div>
                    ) : null}

                    {group.display.map((item, index) => (
                        // Carries no data and has no field behind it. A palette of TYPES could not
                        // express these, which is why the designer's palette is widgets.
                        <div key={`d${index}`} style={{maxWidth: 720}}>
                            {item.Kind === "divider" ? (
                                <hr style={{border: 0, borderTop: "1px solid #E6EAF0", margin: 0}}/>
                            ) : item.Kind === "heading" ? (
                                <Typography style={{fontSize: 14, fontWeight: 600, color: Grey17}}>
                                    {item.Text}
                                </Typography>
                            ) : (
                                <Typography style={{fontSize: 13, color: Grey6}}>{item.Text}</Typography>
                            )}
                        </div>
                    ))}

                    <div style={{display: "flex", flexWrap: "wrap", gap: 18}}>
                    {group.fields.map(field => {
                    // Inherited means declared by a type ABOVE this record's own. A field the leaf
                    // declared carries provenance too, and saying "from Self-Tapping Screw" on a
                    // self-tapping screw would be noise.
                    const inheritedFrom = field.DeclaredBy && field.DeclaredBy.Name !== leafTypeName
                        ? field.DeclaredBy.Name : null;
                    return (
                <div key={field.Code}
                     style={{flexBasis: field.Control === "Table" ? "100%" : WIDTHS[field.Width ?? "full"],
                             flexGrow: 0, minWidth: 0,
                             maxWidth: field.Control === "Table" ? "100%" : 720}}>
                    <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17, marginBottom: 4}}>
                        {field.Label}
                        {field.Required && !field.Retired ? (
                            // Retired wins: the question is no longer asked, so marking it required
                            // would demand an answer to something nobody is being shown.
                            <span style={{color: "#C0392B", marginLeft: 3}} aria-label="required">*</span>
                        ) : null}
                        {field.Retired ? (
                            <span style={{fontWeight: 400, color: Grey6}}> · retired</span>
                        ) : null}
                        {field.Diverged ? (
                            // Marked WHERE IT IS DRAWN. A field this record invented looks exactly
                            // like every other one, which is the problem: nothing enumerates it, no
                            // rule was written against it, and no catalogue has a column for it.
                            <span style={{fontWeight: 400, color: "#C9820A"}}> · only on this record</span>
                        ) : null}
                        {inheritedFrom ? (
                            // WHERE THE QUESTION CAME FROM, for the same reason the line above
                            // exists: a question inherited from a type this one specialises looks
                            // exactly like one the record's own type asked, and nobody reading the
                            // form can tell. Grey rather than amber -- this is provenance, not a
                            // problem.
                            <span style={{fontWeight: 400, color: Grey6}}> · from {inheritedFrom}</span>
                        ) : null}
                    </Typography>

                    {field.Unavailable ? (
                        <Typography style={{fontSize: 13, color: "#C9820A"}}>
                            This field's type cannot resolve its values ({field.Unavailable}).
                        </Typography>
                    ) : field.RequiresContext.length > 0 && field.Options.length === 0 ? (
                        // The dependency, said out loud. An empty picker with no explanation is what
                        // the form this was modelled on shipped, and nobody could tell why.
                        //
                        // Only when there is NOTHING to show. If the record already holds values the
                        // control is drawn anyway: clearing the parent must not make the child's
                        // contents invisible, and the note below says why nothing can be added.
                        <Typography style={{fontSize: 13, color: Grey6}}>
                            {waitingOn(field, fields)}
                        </Typography>
                    ) : (
                        <>
                            {field.Control === "Table" ? (
                                <RepeatingGroup field={field}
                                                value={values[field.Code]}
                                                problems={nestedProblems(problems, field.Code)}
                                                readOnly={!!readOnly || !!field.ReadOnly}
                                                onChange={(next: any) => set(field.Code, next)}/>
                            ) : (
                                <Control field={field}
                                         value={values[field.Code]}
                                         readOnly={!!readOnly || !!field.ReadOnly}
                                         objectRevisionUSID={objectRevisionUSID}
                                         onChange={(next: any) => set(field.Code, next)}/>
                            )}
                            {field.RequiresContext.length > 0 ? (
                                <Typography style={{fontSize: 12, color: Grey6, marginTop: 4}}>
                                    {waitingOn(field, fields)}
                                </Typography>
                            ) : null}
                        </>
                    )}

                    {ownProblems(problems, field.Code).map((problem, i) => (
                        // WHAT THE SERVER SAID, not a re-derivation of it. The client does not
                        // evaluate the rules, so it has nothing of its own to say here -- and a
                        // message it invented could contradict the one that actually blocked the save.
                        <Typography key={i} style={{fontSize: 12, color: "#C0392B", marginTop: 4}}>
                            {problem.Detail}
                        </Typography>
                    ))}

                    {field.Help || field.Description ? (
                        <Typography style={{fontSize: 12, color: Grey6, marginTop: 4}}>
                            {field.Help || field.Description}
                        </Typography>
                    ) : null}
                </div>
                    );
                    })}
                    </div>
                </div>
                ))}
                </div>
                );
            })}
        </div>
    );
}

// A width is a share of the row, so two halves sit side by side. Capped at 720 per field, because a
// single input stretched across a wide screen reads badly whatever the form asked for.
// BETWEEN SECTIONS, NOT WITHIN THEM. 18px is right between a section's own fields and too tight
// between one section and the next: each section ends with a rule and the next begins with one, so
// at 18 the two read as a single thick divider -- and an outlined section, which draws 10px
// outside its own box, overlapped its neighbour's heading. The fields inside a section keep 18.
const SECTION_GAP = 38;

const WIDTHS: Record<string, string> = {
    full: "100%", half: "calc(50% - 9px)", third: "calc(33.333% - 12px)",
    // Paired with `third` across one 18px gap: 33.333% - 12 plus 66.667% - 6 is the full row.
    "two-thirds": "calc(66.667% - 6px)",
};

// What an option is called. A held value the picker no longer offers still has a name, because the
// server resolved it by key; only a genuine orphan has none, and saying so beats a UUID.
const UNNAMED = "Unrecognised value";

function displayOf(field: any, value: any) {
    const option = field.Options.find((o: any) => o.Value === value);
    return option?.Display ?? UNNAMED;
}

function waitingOn(field: Field, fields: Field[]) {
    const labels = field.RequiresContext
        .map(code => fields.find(f => f.Code === code)?.Label ?? code)
        .join(" and ");
    return `Choose ${labels} first.`;
}

export function Control({field, value, onChange, readOnly, objectRevisionUSID}: any) {
    const common = {size: "small" as const, fullWidth: true, disabled: readOnly};

    switch (field.Control) {
        case "File":
            // THE ANSWER IS NOT IN `value`, and nothing here writes one. A File field's answer is an
            // ATTACHMENT_LINK row carrying this field's code, so the control talks to the attachment
            // API directly and the form's values never learn about it -- which is also why the server
            // skips File fields when it projects FORM_VALUE.
            //
            // Nothing to attach to until the record exists: a shape being PREVIEWED has no record, so
            // the field says what it is rather than offering an upload that has nowhere to go.
            return objectRevisionUSID ? (
                <Attachments sourceUSID={objectRevisionUSID}
                             fieldCode={field.Code}
                             single={(field.Cardinality ?? "One") !== "Many"}
                             readOnly={readOnly}/>
            ) : (
                <Typography style={{fontSize: 13, color: Grey6}}>
                    A file — uploaded once this record exists.
                </Typography>
            );

        case "Table":
            // Reached only from inside a grid cell -- a composite nested in a composite. Drawn as a
            // note rather than a grid inside a grid, which is a layout nobody can read; the values
            // are still carried, still validated and still projected.
            return (
                <Typography style={{fontSize: 13, color: Grey6}}>
                    A table of {field.TypeName} — not drawn inside a row.
                </Typography>
            );

        case "YesNo":
            return (
                <div style={{display: "flex", alignItems: "center", gap: 6}}>
                    <Checkbox checked={!!value} disabled={readOnly}
                              onChange={(e) => onChange(e.target.checked)}/>
                    <Typography style={{fontSize: 14, color: Grey17}}>{value ? "Yes" : "No"}</Typography>
                </div>
            );

        case "Date":
            // THE DAY THE ANSWER NAMES, WHATEVER IT ARRIVED AS. A browser date input blanks any
            // value that is not exactly YYYY-MM-DD, and SA-00001's answers held
            // "2026-01-29 09:00:00" -- every date in the Peak sample data carried a time -- so the
            // form drew its dates empty while the projection, which the database had parsed, showed
            // them everywhere else. Worse, the empty input meant the next save sent "" and lost
            // the date. Drawn as the day; written as the day (the save normalises too).
            return <TextField {...common} type="date" value={dayOf(value)}
                              onChange={(e) => onChange(e.target.value)}/>;

        case "Number":
            return <TextField {...common} type="number" value={value ?? ""}
                              onChange={(e) => onChange(e.target.value)}/>;

        case "LongText":
            return <TextField {...common} multiline rows={3} value={value ?? ""}
                              onChange={(e) => onChange(e.target.value)}/>;

        case "Checkboxes":
        case "Radio": {
            // THE SAME DATA AS Select / MultiSelect, drawn open instead of collapsed. Worth it for a
            // short fixed list: every choice is visible without opening anything, and it survives
            // being printed.
            const many = field.Control === "Checkboxes";
            const chosen: any[] = many ? (Array.isArray(value) ? value : []) : [value];

            const toggle = (option: any, on: boolean) => {
                if (!many) return onChange(on ? option.Value : null);
                const next = on ? [...chosen, option.Value]
                                : chosen.filter((v: any) => v !== option.Value);
                onChange(next);
            };

            if (field.Options.length === 0) {
                return <Typography style={{fontSize: 13, color: Grey6}}>No options</Typography>;
            }

            return (
                <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                    {field.Options.map((option: any) => {
                        const on = chosen.some((v: any) => v === option.Value);
                        // A value the picker would no longer offer is drawn CHECKED AND DISABLED
                        // with its reason beside it -- the same rule the dropdown follows. An
                        // unchecked greyed box would be ambiguous: off, or unavailable?
                        const retired = option.Offered === false;
                        const Box = many ? Checkbox : Radio;
                        return (
                            <FormControlLabel
                                key={option.Value}
                                disabled={readOnly || (retired && !on)}
                                control={<Box size="small" checked={on}
                                                  onChange={(e) => toggle(option, e.target.checked)}/>}
                                label={
                                    <span style={{fontSize: 13, color: Grey17}}>
                                        {option.Display ?? UNNAMED}
                                        {retired ? (
                                            <span style={{color: Grey6, marginLeft: 6, fontSize: 12}}>
                                                · {option.Orphan ? "no longer exists" : "no longer offered"}
                                            </span>
                                        ) : null}
                                    </span>
                                }
                            />
                        );
                    })}
                </div>
            );
        }

        case "Select":
        case "MultiSelect": {
            const many = field.Control === "MultiSelect";
            // A FIELD THAT PICKS RECORDS opens the classification's own list screen -- Filter By,
            // visibility, search, paging -- the same picker as the contract's purchase orders
            // (Ed, 2026-10-28: "Use the same picker for the contract's Items").
            if (picksFromListScreen(field.PicksObjects)) {
                return <FormObjectPicker label={field.Label} classificationCode={field.PicksObjects!}
                                         options={field.Options} many={many}
                                         value={value} onChange={onChange} disabled={readOnly}/>;
            }
            // A LONG LIST OPENS A DIALOG, the form engine's LongSelect. A dropdown of eighty purchase
            // orders scrolls, but it cannot be searched and the chosen ones cannot be reviewed; past
            // LONG_LIST options the field opens a searchable list with a Select button instead.
            if (field.Options.length > LONG_LIST) {
                return <FormLongSelect label={field.Label} options={field.Options} many={many}
                                       value={value} onChange={onChange} disabled={readOnly}/>;
            }
            return (
                <Select {...common} displayEmpty multiple={many}
                        value={many ? (Array.isArray(value) ? value : []) : (value ?? "")}
                        onChange={(e) => onChange(e.target.value)}
                        renderValue={(selected: any) => {
                            const chosen = many ? selected : [selected];
                            const labels = chosen
                                .filter((v: any) => v !== "" && v != null)
                                .map((v: any) => displayOf(field, v));
                            return labels.length ? labels.join(", ")
                                : <span style={{color: Grey6}}>Select</span>;
                        }}>
                    {field.Options.length === 0 ? (
                        <MenuItem disabled value="">No options</MenuItem>
                    ) : null}
                    {field.Options.map((option: any) => (
                        // A value the picker would no longer offer is DISABLED rather than absent:
                        // visible so it can be read and removed, unselectable so it cannot come back.
                        <MenuItem key={option.Value} value={option.Value}
                                  disabled={option.Offered === false}>
                            {option.Display ?? UNNAMED}
                            {option.Offered === false ? (
                                <span style={{color: Grey6, marginLeft: 6, fontSize: 12}}>
                                    · {option.Orphan ? "no longer exists" : "no longer offered"}
                                </span>
                            ) : null}
                        </MenuItem>
                    ))}
                </Select>
            );
        }

        default:
            return <TextField {...common} value={value ?? ""}
                              onChange={(e) => onChange(e.target.value)}/>;
    }
}
